import { useFormik } from 'formik';
import { Flex, Stack } from "@chakra-ui/react";
import CustomInput from "../../form-components/CustomInput";
import SubmitButton from "../../custom/SubmitButton";
import { UseApi } from '../../../libraries/UseApi';
import { UserInfoContext } from '../../../provider/UserInfoProvider';
import * as Yup from 'yup';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../../form-components/CustomSelect';
import { GetBranchOptions, GetContractTypeOptions, GetCountryOptions, GetDepartmentOptions, GetSexOptions, GetUserTypeOptions } from '../../options/SelectOptions';
import { UseMessage } from '../../../libraries/UseMessage';
import CustomRadioButton from '../../form-components/CustomRadioButton';
import CustomTextarea from '../../form-components/CustomTextarea';

const AddUserForm = ({user = {}, isEdit=false}) => {
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [countries, setCountries] = useState([]);

    const { postApi, patchApi, getApi } = UseApi();
    const {token} = useContext(UserInfoContext);
    const navigate = useNavigate();
    const {Message} = UseMessage();

    const getBranchDepartmentCountryData = () => {
        getApi(
            `${process.env.REACT_APP_API_URL}/common/branch-department-country-data`, {}, { token: token },
            (response) => {
                // 成功時
                if (response.status !== "success") {
                    // error
                    alert(Message.Common.ERROR);
                    return;
                }

                setBranches(response.data?.branches);
                setDepartments(response.data?.departments);
                setCountries(response.data?.countries);
            },
            () => {}
        );
    };

    useEffect(() => {
        getBranchDepartmentCountryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    const formik = useFormik({
        initialValues: {
            name: user?.name,
            name_kana: user?.name_kana,
            email: user?.email,
            user_type: user?.user_type,
            branch_id: user?.branch_id,
            department_id: user?.department_id,
            country_id: user?.country_id,
            contract_type: user?.contract_type,
            sex: user?.sex,
            dob: user?.dob,
            available_from: user?.available_from,
            short_description: user?.short_description,
            thumb: user?.thumb,
            post_code: user?.post_code,
            address: user?.address,
            nearly_station: user?.nearly_station,
            way_to_nearly_station: user?.way_to_nearly_station,
            joining_date: user?.joining_date,
            retirement_date: user?.retirement_date
        },
        validationSchema: Yup.object({
            name: Yup.string().required('氏名は必修'),
            name_kana: Yup.string().required('氏名カナは必修'),
            email: Yup.string().email().required('正しいメールアドレスを入力してください'),
            user_type: Yup.string().oneOf(['1','2','3'],'ユーザタイプを選択してください').required('ユーザタイプを選択してください'),
            branch_id: Yup.string().required('支社を選択してください'),
            department_id: Yup.string().required('部署を選択してください'),
            country_id: Yup.string().required('国を選択してください'),
            contract_type: Yup.string().oneOf(['1','2'],'契約タイプを選択してください').required('契約タイプを選択してください'),
            dob: Yup.string().required('生年月日を選択してください'),
            sex: Yup.string().oneOf(['1','2'],'契約タイプを選択してください'),
        }),
        onSubmit: (values) => {
            if(isEdit) {
                patchApi(
                    `${process.env.REACT_APP_API_URL}/user/${user?.id}`, values, { token: token },
                    (response) => {
                        // レスポンスチェック
                        if (response.isSuccess === false) {
                            // 入力内容を確認してください。
                            //alert(Message.Common.CONFIRM_INPUT);
                            return;
                        }
                        navigate('/users');
                    }
                );
            } else {
                postApi(
                    `${process.env.REACT_APP_API_URL}/user`, values, { token: token },
                    (response) => {
                        // レスポンスチェック
                        if (response.isSuccess === false) {
                            // 入力内容を確認してください。
                            //alert(Message.Common.CONFIRM_INPUT);
                            return;
                        }
                        navigate('/users');
                    }
                );
            }
        }
    });
    
    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack spacing={4} key={'addUser'}>
                <CustomInput
                    formik={formik}
                    label={'姓名'}
                    type={'text'}
                    name={'name'}
                    id={'name'}
                    placeholder={'姓名を入力'}
                    required={true}
                />
                <CustomInput
                    formik={formik}
                    label={'姓名カナ分類'}
                    type={'text'}
                    name={'name_kana'}
                    id={'name_kana'}
                    placeholder={'姓名カナを入力'}
                    required={true}
                />
                <CustomInput
                    formik={formik}
                    label={'メールアドレス'}
                    type={'text'}
                    name={'email'}
                    id={'email'}
                    placeholder={'メールアドレスを入力'}
                    required={true}
                    disabled={isEdit}
                />
                <CustomSelect
                    formik={formik}
                    label={'ユーザタイプ'}
                    name={'user_type'}
                    id={'user_type'}
                    options={GetUserTypeOptions()}
                    required={true}
                />
                <CustomSelect
                    formik={formik}
                    label={'支社'}
                    name={'branch_id'}
                    id={'branch_id'}
                    options={GetBranchOptions(branches)}
                    required={true}
                />
                <CustomSelect
                    formik={formik}
                    label={'部署'}
                    name={'department_id'}
                    id={'department_id'}
                    options={GetDepartmentOptions(departments)}
                    required={true}
                />
                <CustomSelect
                    formik={formik}
                    label={'国'}
                    name={'country_id'}
                    id={'country_id'}
                    options={GetCountryOptions(countries)}
                    required={true}
                />
                <CustomRadioButton
                    formik={formik}
                    label={'契約タイプ'}
                    name={'contract_type'}
                    id={'contract_type'}
                    options={GetContractTypeOptions()}
                    required={true}
                />
                <CustomInput
                    formik={formik}
                    label={'生年月日'}
                    type={'date'}
                    name={'dob'}
                    id={'dob'}
                    required={true}
                />
                <CustomRadioButton
                    formik={formik}
                    label={'性別'}
                    name={'sex'}
                    id={'sex'}
                    options={GetSexOptions()}
                />
                <CustomInput
                    formik={formik}
                    label={'参画可能日'}
                    type={'date'}
                    name={'available_from'}
                    id={'available_from'}
                />
                <CustomTextarea
                    formik={formik}
                    label={'自己'}
                    name={'short_description'}
                    id={'short_description'}
                />
                <CustomInput
                    formik={formik}
                    label={'最寄駅'}
                    type={'text'}
                    name={'nearly_station'}
                    id={'nearly_station'}
                    placeholder={'東京駅'}
                />
                <CustomInput
                    formik={formik}
                    label={'自宅から最寄駅まで'}
                    type={'text'}
                    name={'nearly_station'}
                    id={'nearly_station'}
                    placeholder={'。。駅まで歩いて5分'}
                />
                <CustomInput
                    formik={formik}
                    label={'郵便番号'}
                    type={'number'}
                    name={'post_code'}
                    id={'post_code'}
                    placeholder={'*******'}
                />
                <CustomTextarea
                    formik={formik}
                    label={'住所'}
                    name={'address'}
                    id={'address'}
                    placeholder={''}
                />
                <CustomInput
                    formik={formik}
                    label={'入社日'}
                    type={'date'}
                    name={'joining_date'}
                    id={'joining_date'}
                />
                <CustomInput
                    formik={formik}
                    label={'退職日'}
                    type={'date'}
                    name={'retirement_date'}
                    id={'retirement_date'}
                />
                <Stack spacing={10}>
                    <Flex gap={2}>
                        <SubmitButton buttonText={isEdit ? '編集' : '登録'} />
                    </Flex>
                </Stack>
            </Stack>
        </form>
    );
}

export default AddUserForm;